import { MainTabsV2 } from '@labrav/react-components'
import { scrollspyTabs, ScrollToFieldError } from '@labrav/common'
import { useStyles } from './AzurePrivateOfferCreationForm.styles'
import { useTheme } from '@mui/material'
import { MicrosoftOfferDetail } from './MicrosoftOfferDetail'
import { useFormikContext } from 'formik'
import React, { useMemo, useState } from 'react'
import type { Product } from '../../../modules/productsListing/types'
import { getIsErrorForMPPOOfferTab } from './getIsErrorForMPPOOfferTab'
import { ProductNameSection } from '../../../common/components/ProductNameSection/ProductNameSection'
import { useParams } from 'react-router-dom'
import { useSelector } from '../../../store'
import type { MarketplaceTemplateDataResponse } from '../../../types/mappingManager'
import type {
  FieldMetadata,
  FieldMetadataField,
} from '../../../modules/privateOffer/types'
import { ContractDuration } from './ContractDuration/ContractDuration'
import { PricingConfiguration } from './ContainerPlan/ContainerPlan'
import { Renewal } from './Renewal/Renewal'
import { Eula } from './Eula/Eula'
import { Stakeholders } from './Stakeholders/Stakeholders'
import { CustomMetaData } from './CustomMetaData/CustomMetaData'
import {
  MicrosoftFormFAQs,
  MicrosoftFormFAQsItem,
} from './MicrosoftFormFAQs/MicrosoftFormFAQs'
import type { StakeholderUserOption } from '../../../common/components/ViewStakeholders/stakeholderUserOption'
import { ClientSecretExpirationBanner } from '../../ClientSecretExpirationBanner/ClientSecretExpirationBanner'

export type PrivateOfferCreationFormProps = {
  product: Product
  tabTop?: string
  isSalesforceContext?: boolean
  stakeholderUsers?: StakeholderUserOption[]
}

const OfferDetailsFaqs = () => (
  <MicrosoftFormFAQs item={MicrosoftFormFAQsItem.AUTHORIZATION_DETAIL} />
)

export const PrivateOfferCreationForm = ({
  product,
  tabTop,
  isSalesforceContext = false,
  stakeholderUsers = [],
}: PrivateOfferCreationFormProps) => {
  const offset = 260
  const { classes } = useStyles({ offset })
  const theme = useTheme()
  const { accountId: accountIdFromParams } = useParams<{ accountId: string }>()
  const accountId = accountIdFromParams
  const formikProps = useFormikContext<Record<string, unknown>>()
  const [eulaErr, setEulaErr] = useState<string>('')

  const templateData = useSelector(
    state => state.marketplaceMappingManager?.templateData?.templateData
  ) as MarketplaceTemplateDataResponse | undefined
  const fieldMetadata = useSelector(
    state =>
      state.privateOffersInCreation.currentPrivateOfferInCreation[
        product.productId
      ]?.fieldMetadata
  ) as FieldMetadata | undefined

  const fieldMetadataMetaData =
    templateData?.fieldMappings?.fields?.fieldsMapping?.Labra__Metadata_Hide__c
      ?.isHidden
  const fieldMetadataStakeholders =
    templateData?.fieldMappings?.fields?.fieldsMapping?.Labra__Stakeholders__c
      ?.isHidden

  const isEulaFileReadOnly = (fieldMetadata?.eulaFiles as FieldMetadataField)
    ?.readOnly
  const isEulaFileHidden = (fieldMetadata?.eulaFiles as FieldMetadataField)
    ?.hidden
  const isEulaTypeReadOnly = (fieldMetadata?.eulaType as FieldMetadataField)
    ?.readOnly
  const isEulaTypeHidden = (fieldMetadata?.eulaType as FieldMetadataField)
    ?.hidden

  const isPurchaseTermsHidden = (
    fieldMetadata?.purchaseTerms as FieldMetadataField
  )?.hidden

  const shouldRenderEulaSection = useMemo(
    () => !(isEulaFileHidden && isEulaTypeHidden),
    [isEulaFileHidden, isEulaTypeHidden]
  )
  const isRenewalHidden = (
    fieldMetadata?.existingContractRenewal as FieldMetadataField
  )?.hidden

  const privateOfferCreationTabs = [
    {
      key: 'offer-details',
      label: 'Offer details',
      renderElement: (
        <div
          id={'offer-details'}
          data-testid={'offer-details'}
          className={classes.scrollTopMargin}
        >
          <MicrosoftOfferDetail
            formikProps={formikProps}
            product={product}
            isSalesforceContext={isSalesforceContext}
            offerDetailsFaqs={<OfferDetailsFaqs />}
          />
          {!isPurchaseTermsHidden && (
            <ContractDuration product={product} fieldMetadata={fieldMetadata} />
          )}
        </div>
      ),
      isError: getIsErrorForMPPOOfferTab({
        tabName: 'offer-details',
        formikValues: formikProps,
        mppo: false,
      }),
    },
    {
      key: 'offer-pricing',
      label: 'Pricing',
      renderElement: (
        <div
          id={'offer-pricing'}
          data-testid={'offer-pricing'}
          className={classes.scrollTopMargin}
        >
          <PricingConfiguration product={product} validate={true} />
        </div>
      ),
      isError: getIsErrorForMPPOOfferTab({
        tabName: 'offer-pricing',
        formikValues: formikProps,
        mppo: false,
      }),
    },
    {
      key: 'offer-other',
      label: 'Other',
      renderElement: (
        <div
          id={'offer-other'}
          data-testid={'offer-other'}
          className={classes.scrollTopMargin}
        >
          {!isRenewalHidden && <Renewal product={product} />}
          {shouldRenderEulaSection && (
            <Eula
              eulaErr={eulaErr}
              setEulaErr={setEulaErr}
              showEulaOptions={isEulaTypeHidden ? false : true}
              disableEulaFileUpload={isEulaFileReadOnly}
              showEulaFileUpload={isEulaFileHidden ? false : true}
              disableEulaOptions={isEulaTypeReadOnly}
            />
          )}
          {!fieldMetadataStakeholders && (
            <Stakeholders
              hideStakeholders={isSalesforceContext}
              users={stakeholderUsers}
            />
          )}
          {!fieldMetadataMetaData && <CustomMetaData product={product} />}
        </div>
      ),
      isError: getIsErrorForMPPOOfferTab({
        tabName: 'offer-other',
        formikValues: formikProps,
        eulaErr,
        mppo: false,
      }),
    },
  ]
  const { activeTabInex, scrollToSection } = scrollspyTabs({
    tabs: privateOfferCreationTabs,
    offset: offset,
  })

  return (
    <div className={classes.root}>
      <ClientSecretExpirationBanner variant="banner" accountId={accountId} />
      <ScrollToFieldError customErrorFields={eulaErr ? ['eulaFiles'] : []} />
      <ProductNameSection product={product} />
      <MainTabsV2
        activeTabIndex={activeTabInex}
        onChange={scrollToSection}
        tabs={privateOfferCreationTabs}
        backGroundColor={theme.palette.textBackground.light}
        tabPosition="sticky"
        tabTop={tabTop}
      />
      {privateOfferCreationTabs.map(tabItem => tabItem.renderElement)}
    </div>
  )
}
